import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useApp } from '../hooks/useApp'
import URLInput from '../components/URLInput'
import JobsHistory from '../components/JobsHistory'
import Modal from '../components/Modal'
import SubscriptionStatus from '../components/SubscriptionStatus'
import { isJobProcessing, isJobSuccessful } from '../utils/jobStatus'

function DashboardPage() {
  const { user, isDevelopmentMode } = useAuth()
  const { jobs, activeProcessing, loadJobsHistory } = useApp()
  const [showFileViewer, setShowFileViewer] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  // Load jobs on mount
  useEffect(() => {
    loadJobsHistory()
  }, [])


  const handleFileView = (file) => {
    setSelectedFile(file)
    setShowFileViewer(true)
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
        <div className="space-y-8">
          <URLInput />
          <SubscriptionStatus />
          <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
            <JobsHistory jobs={jobs} onFileView={handleFileView} />
          </div>
          {jobs.length > 0 && (
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Quick Stats
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Completed</p>
                      <p className="text-lg font-semibold">
                        {jobs.filter((j) => isJobSuccessful(j.status)).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Processing</p>
                      <p className="text-lg font-semibold">
                        {
                          jobs.filter((j) => isJobProcessing(j.status)).length
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* File Viewer Modal */}
      {showFileViewer && selectedFile && (
        <Modal
          title="File Viewer"
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          size="lg"
        >
          <FileViewer
            file={selectedFile}
            onClose={() => setShowFileViewer(false)}
          />
        </Modal>
      )}
    </div>
  );
}

// Temporary placeholder components - will create these next
function FileViewer({ file, onClose }) {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadFileContent = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Extract job ID from file object
        const jobId = file.jobId
        
        console.log('[FileViewer] Debug info:', {
          file,
          jobId,
          fileName: file.name,
          authToken: localStorage.getItem('auth_token') ? 'present' : 'missing'
        })
        
        if (!jobId) {
          throw new Error(`Job ID not found. File object: ${JSON.stringify(file)}`)
        }

        const authToken = localStorage.getItem('auth_token')
        if (!authToken) {
          // For development, show a helpful message about creating a demo auth token
          throw new Error('Authentication required - No auth token found. For development, you would need to implement login or use a demo token.')
        }

        const response = await fetch(`/api/v1/jobs/${jobId}/files/${file.name}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          const errorText = await response.text()
          let errorMessage = `Failed to load file: ${response.status}`
          
          try {
            const errorData = JSON.parse(errorText)
            if (errorData.error) {
              errorMessage = `${errorData.error} (${response.status})`
            }
          } catch (e) {
            // If not JSON, use the text directly
            if (errorText) {
              errorMessage = `${errorText} (${response.status})`
            }
          }
          
          throw new Error(errorMessage)
        }

        const data = await response.json()
        
        if (data.success && data.data) {
          setContent(data.data.content)
        } else {
          throw new Error('Invalid response format')
        }
      } catch (error) {
        console.error('Error loading file content:', error)
        setError(error.message)
        setContent('')
      } finally {
        setLoading(false)
      }
    }

    if (file) {
      loadFileContent()
    }
  }, [file])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{file?.name || 'File'}</h3>
        <div className="flex space-x-2">
          <button 
            onClick={() => navigator.clipboard.writeText(content)}
            className="btn btn-primary text-sm"
            disabled={!content}
          >
            Copy
          </button>
        </div>
      </div>
      
      <div className="bg-background p-4 rounded-lg max-h-96 overflow-y-auto">
        {loading ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-muted-foreground">Loading file content...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-2">⚠️</div>
            <p className="text-red-600 text-sm">{error}</p>
            {(error.includes('Invalid or expired token') || error.includes('Authentication required')) && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-yellow-700 text-xs mb-2">
                  <strong>Authentication Issue:</strong> You need to log in to view files. 
                  The app requires authentication to access job files.
                </p>
                <button 
                  onClick={() => {
                    // For development - set a demo token and try again
                    localStorage.setItem('auth_token', 'demo-token-for-development')
                    window.location.reload()
                  }}
                  className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                >
                  Set Demo Token (Development Only)
                </button>
              </div>
            )}
            <button 
              onClick={() => window.location.reload()} 
              className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Try refreshing the page
            </button>
          </div>
        ) : content ? (
          <div 
            className="text-sm prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: content.includes('<') ? content : content.replace(/\n/g, '<br>') }}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No content available</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardPage